import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// ─── Campaigns ───────────────────────────────────────────
export const fetchCampaigns = createAsyncThunk(
  'marketing/fetchCampaigns',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/campaigns', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch campaigns');
    }
  }
);

export const createCampaign = createAsyncThunk(
  'marketing/createCampaign',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/campaigns', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to create campaign');
    }
  }
);

export const updateCampaign = createAsyncThunk(
  'marketing/updateCampaign',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/marketing/campaigns/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to update campaign');
    }
  }
);

export const deleteCampaign = createAsyncThunk(
  'marketing/deleteCampaign',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/marketing/campaigns/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete campaign');
    }
  }
);

// ─── Automations ─────────────────────────────────────────
export const fetchAutomations = createAsyncThunk(
  'marketing/fetchAutomations',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/automations', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch automations');
    }
  }
);

export const createAutomation = createAsyncThunk(
  'marketing/createAutomation',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/automations', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to create automation');
    }
  }
);

export const updateAutomation = createAsyncThunk(
  'marketing/updateAutomation',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/marketing/automations/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to update automation');
    }
  }
);

export const deleteAutomation = createAsyncThunk(
  'marketing/deleteAutomation',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/marketing/automations/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete automation');
    }
  }
);

// ─── Segments ────────────────────────────────────────────
export const fetchSegments = createAsyncThunk(
  'marketing/fetchSegments',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/segments', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch segments');
    }
  }
);

export const createSegment = createAsyncThunk(
  'marketing/createSegment',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/segments', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to create segment');
    }
  }
);

export const updateSegment = createAsyncThunk(
  'marketing/updateSegment',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/marketing/segments/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to update segment');
    }
  }
);

export const deleteSegment = createAsyncThunk(
  'marketing/deleteSegment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/marketing/segments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete segment');
    }
  }
);

export const previewSegment = createAsyncThunk(
  'marketing/previewSegment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/marketing/segments/${id}/preview`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to preview segment');
    }
  }
);

// ─── Dashboard & Leads ───────────────────────────────────
export const fetchMarketingDashboard = createAsyncThunk(
  'marketing/fetchDashboard',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/dashboard/metrics', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch dashboard metrics');
    }
  }
);

export const fetchMarketingAnalytics = createAsyncThunk(
  'marketing/fetchAnalytics',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/analytics', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch analytics');
    }
  }
);

// ─── Initial State ───────────────────────────────────────
const initialState = {
  campaigns: [],
  automations: [],
  segments: [],
  dashboard: null,
  analytics: null,
  previewCount: 0,
  loading: false,
  error: null,
};

// ─── Slice ──────────────────────────────────────────────
const marketingSlice = createSlice({
  name: 'marketing',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Campaigns
    builder
      .addCase(fetchCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(createCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns.unshift(action.payload);
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updateCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.campaigns.findIndex(c => c._id === action.payload._id);
        if (idx >= 0) state.campaigns[idx] = action.payload;
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(deleteCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = state.campaigns.filter(c => c._id !== action.payload);
      })
      .addCase(deleteCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Automations
    builder
      .addCase(fetchAutomations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAutomations.fulfilled, (state, action) => {
        state.loading = false;
        state.automations = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAutomations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(createAutomation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAutomation.fulfilled, (state, action) => {
        state.loading = false;
        state.automations.unshift(action.payload);
      })
      .addCase(createAutomation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updateAutomation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAutomation.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.automations.findIndex(a => a._id === action.payload._id);
        if (idx >= 0) state.automations[idx] = action.payload;
      })
      .addCase(updateAutomation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(deleteAutomation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAutomation.fulfilled, (state, action) => {
        state.loading = false;
        state.automations = state.automations.filter(a => a._id !== action.payload);
      })
      .addCase(deleteAutomation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Segments
    builder
      .addCase(fetchSegments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSegments.fulfilled, (state, action) => {
        state.loading = false;
        state.segments = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchSegments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(createSegment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSegment.fulfilled, (state, action) => {
        state.loading = false;
        state.segments.unshift(action.payload);
      })
      .addCase(createSegment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updateSegment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSegment.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.segments.findIndex(s => s._id === action.payload._id);
        if (idx >= 0) state.segments[idx] = action.payload;
      })
      .addCase(updateSegment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(deleteSegment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSegment.fulfilled, (state, action) => {
        state.loading = false;
        state.segments = state.segments.filter(s => s._id !== action.payload);
      })
      .addCase(deleteSegment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(previewSegment.pending, (state) => {
        state.loading = true;
      })
      .addCase(previewSegment.fulfilled, (state, action) => {
        state.loading = false;
        state.previewCount = action.payload.count || 0;
      })
      .addCase(previewSegment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Dashboard & Leads
    builder
      .addCase(fetchMarketingDashboard.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMarketingDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchMarketingDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchMarketingAnalytics.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMarketingAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchMarketingAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = marketingSlice.actions;
export default marketingSlice.reducer;
